import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, of, ReplaySubject, Subject, tap } from 'rxjs';
import { environment } from '~env';
import { IUser, IUserRequest } from '~models/interfaces/user.interface';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  public usersListLoading: Subject<boolean> = new Subject<boolean>();

  public userRolesLoading: Subject<boolean> = new Subject<boolean>();

  public userPrivilegesLoading: Subject<boolean> = new Subject<boolean>();

  public userDataLoading: Subject<boolean> = new Subject<boolean>();

  private userInfo$: ReplaySubject<IUser> = new ReplaySubject<IUser>(1);

  private userPrivileges$: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);

  private readonly apiUrl = environment.apiUrl;

  constructor(private httpClient: HttpClient) { }

  public me(): Observable<IUser> {
    return this.httpClient.get<IUser>(`${this.apiUrl}/api/user/me`).pipe(
      tap((data) => this.userInfo$.next(data))
    );
  }

  /** Получить данные текущего пользователя */
  public getUserProfile(): Observable<IUser> {
    return this.userInfo$.asObservable();
  }

  /** Получить данные пользователя по id */
  public getUserDataById(id: number): Observable<IUser | null> {
    this.userDataLoading.next(true);

    return this.httpClient.get<IUser>(`${this.apiUrl}/api/user/get/${id}`).pipe(
      catchError((error) => {
        console.error(error);
        return of(null);
      }),
      finalize(() => this.userDataLoading.next(false))
    );
  }

  public getUserPrivileges(): Observable<string[]> {
    return this.userPrivileges$.asObservable();
  }

  public getUserList(): Observable<IUser[]> {
    this.usersListLoading.next(true);

    return this.httpClient.get<IUser[]>(`${this.apiUrl}/api/user/all`).pipe(
      catchError((error) => {
        console.error(error);
        return of([]);
      }),
      finalize(() => this.usersListLoading.next(false))
    );
  }

  public getUserRoles(): Observable<string[]> {
    this.userRolesLoading.next(true);

    return this.httpClient.get<string[]>(`${this.apiUrl}/api/user/available_roles`).pipe(
      catchError((error) => {
        console.error(error);
        return of([]);
      }),
      finalize(() => this.userRolesLoading.next(false))
    );
  }

  public create(body: IUserRequest): Observable<boolean> {
      return this.httpClient.post<IUser>(`${this.apiUrl}/api/user/add`, body).pipe(
        map(() => true),
        catchError((error) => {
          console.error(error);
          return of(false);
        })
      );
  }

  public update(id: number, body: IUserRequest): Observable<boolean> {
      return this.httpClient.put<IUser>(`${this.apiUrl}/api/user/update/${id}`, body).pipe(
        map(() => true),
        catchError((error) => {
          console.error(error);
          return of(false);
        })
      );
  }
  
  public loadPrivileges(): Observable<string[]> {
    this.userPrivilegesLoading.next(true);
    return this.httpClient.get<string[]>(`${this.apiUrl}/api/auth/privileges`).pipe(
      tap((res: string[]) => this.userPrivileges$.next(res)),
      catchError((error) => {
        console.error(error);
        return of([]);
      }),
      finalize(() => this.userPrivilegesLoading.next(false))
    );
  }
}
